// Shared by the browser and the upload route. Kept small so the published server can hold a few at once.
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

export interface FileLook {
    tag: string
    color: string
}

const IMAGES = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'heic', 'heif', 'bmp', 'tif', 'tiff', 'ico'])
const DOCUMENTS = new Set(['doc', 'docx', 'txt', 'rtf', 'odt', 'pages', 'md', 'ppt', 'pptx', 'key', 'odp'])
const SHEETS = new Set(['xls', 'xlsx', 'xlsm', 'csv', 'tsv', 'ods', 'numbers'])

// Tag colours come from the palette tokens in globals.css.
export const TAG_COLORS = {
    pdf: 'var(--color-tag-pdf, #E5383B)',
    image: 'var(--color-tag-image, #3B7BE8)',
    document: 'var(--color-tag-doc, #24459C)',
    sheet: 'var(--color-tag-sheet, #17734A)',
    other: 'var(--color-tag-other, #6B7380)'
} as const

export const describeFile = (name: string, type: string): FileLook => {
    const dot = name.lastIndexOf('.')
    const extension = dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : ''
    const tag = extension ? extension.slice(0, 4).toUpperCase() : 'FILE'

    if (extension === 'pdf' || type === 'application/pdf') return { tag: extension ? tag : 'PDF', color: TAG_COLORS.pdf }
    if (IMAGES.has(extension) || type.startsWith('image/')) return { tag, color: TAG_COLORS.image }
    if (SHEETS.has(extension)) return { tag, color: TAG_COLORS.sheet }
    if (DOCUMENTS.has(extension) || type.startsWith('text/')) return { tag, color: TAG_COLORS.document }

    return { tag, color: TAG_COLORS.other }
}

export const formatSize = (bytes: number): string => {
    const kilobytes = bytes / 1024

    if (kilobytes < 1000) {
        if (kilobytes === 0) return '0 KB'
        if (kilobytes < 10) return `${Math.max(kilobytes, 0.1).toFixed(1).replace(/\.0$/, '')} KB`

        return `${Math.round(kilobytes)} KB`
    }

    const megabytes = kilobytes / 1024

    return `${megabytes < 100 ? megabytes.toFixed(1) : Math.round(megabytes)} MB`
}

// Long names keep their beginning and their extension.
export const shortenName = (name: string, max = 34): string => {
    if (name.length <= max) return name

    const dot = name.lastIndexOf('.')
    const tail = dot > 0 && name.length - dot <= 8 ? name.slice(Math.max(0, dot - 5)) : name.slice(-10)

    return `${name.slice(0, Math.max(4, max - tail.length - 1))}…${tail}`
}

// Upload progress tops out at 92% while bytes travel; the last stretch is the server storing the file.
export const uploadFile = (file: File, onProgress: (fraction: number) => void): Promise<void> => new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()

    request.open('POST', '/api/upload')
    request.timeout = 120000
    request.setRequestHeader('Content-Type', 'application/octet-stream')
    request.setRequestHeader('X-File-Name', encodeURIComponent(file.name))
    request.setRequestHeader('X-File-Type', file.type || 'application/octet-stream')

    request.upload.onprogress = event => {
        if (event.lengthComputable && event.total > 0) onProgress(Math.min(0.92, event.loaded / event.total * 0.92))
    }

    request.onload = () => {
        if (request.status >= 200 && request.status < 300) resolve()
        else reject(new Error(`Upload failed with status ${request.status}`))
    }

    request.onerror = () => reject(new Error('Upload failed'))
    request.onabort = () => reject(new Error('Upload aborted'))
    request.ontimeout = () => reject(new Error('Upload timed out'))
    request.send(file)
})
